import { createContext, type ReactNode, useContext, useMemo } from 'react'
import { Navigate } from 'react-router'

import {
  FeedbackNetworkError,
  FeedbackUnknownError,
} from '@/components/feedbacks'
import { useCompany } from '@/contexts/company'
import { useRouteCompanyId } from '@/hooks/use-route-company-id'
import { useRouteProjectId } from '@/hooks/use-route-project-id'
import type { Bot } from '@/services/fintalk/services/fintalk-backoffice/company-bots/list-by-company'
import type { Project } from '@/services/fintalk/services/fintalk-backoffice/projects/list'

import {
  type GetProjectBotsState,
  useGetProjectBots,
} from './use-get-project-bots'

export interface ProjectContextProps {
  project: Project | null
  bots: Bot[]
  botsStatus: GetProjectBotsState
  loadBots: () => Promise<void>
  updateBotInBots: (bot: Bot) => void
}

const ProjectContext = createContext<ProjectContextProps | null>(null)

export function ProjectProvider({ children }: { children: ReactNode }) {
  const companyId = useRouteCompanyId()!
  const projectId = useRouteProjectId()!

  const { projects, projectsStatus, loadProjects } = useCompany()
  const { bots, botsStatus, loadBots, updateBotInBots } = useGetProjectBots()

  const project = useMemo(
    () => projects.find((project) => project._id === projectId) ?? null,
    [projects, projectId],
  )

  if (
    projectsStatus.type === 'error' &&
    projectsStatus.errorType === 'NETWORK_ERROR'
  ) {
    return (
      <div className="flex min-h-screen w-full items-center justify-center">
        <FeedbackNetworkError
          eventId={projectsStatus.eventId}
          onRetry={loadProjects}
          isRetrying={projectsStatus.isRetrying}
        />
      </div>
    )
  }

  if (projectsStatus.type === 'error') {
    return (
      <div className="flex min-h-screen w-full items-center justify-center">
        <FeedbackUnknownError
          eventId={projectsStatus.eventId}
          onRetry={loadProjects}
          isRetrying={projectsStatus.isRetrying}
        />
      </div>
    )
  }

  if (projectsStatus.type === 'success' && !project) {
    return <Navigate to={`/companies/${companyId}/projects`} replace />
  }

  return (
    <ProjectContext
      value={{ project, bots, botsStatus, loadBots, updateBotInBots }}
    >
      {children}
    </ProjectContext>
  )
}

/**
 * Reads the current project and its bots. Consumers that only need the id
 * must use useRouteProjectId() instead: the URL is the source of truth for
 * which project is selected, this provider only carries its data.
 */
export const useProject = () => {
  const context = useContext(ProjectContext)

  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider')
  }

  return context
}
